import { useReactiveVar } from "@apollo/client";
import { memo, useCallback, useMemo, useState } from "react";
import * as Styled from "./style";
import { DMM_SERVICE_MENU } from "~/components/blocks/layout/global-navi/const/dmm-service-menu";
import { URL } from "~/components/blocks/layout/global-navi/const/url";
import { BusinessTitle } from "~/components/blocks/layout/global-navi/desktop/left/dmm-service/navi-list/business-title";
import { NaviItem } from "~/components/blocks/layout/global-navi/desktop/left/dmm-service/navi-list/navi-item";
import { dmmServiceListDataVar } from "~/components/blocks/layout/store";
import { useViewableRatingBlock } from "~/hooks/use-viewable-rating-block";
import { sendTrackingClickCommon } from "~/lib/tracking/events";
import { useAuth } from "~/provider/auth";

type NavItem = {
  name: string;
  src: string;
  href: string;
  data_link: string;
  isNew: boolean;
};

type DmmServiceList = [NavItem[], NavItem[], NavItem[]];

export const NaviList = memo(() => {
  const dmmServiceListData = useReactiveVar(dmmServiceListDataVar);

  const [isOpenMore, setIsOpenMore] = useState(false);
  const openMore = useCallback(() => setIsOpenMore(true), []);

  const dmmServiceList: DmmServiceList = useMemo(() => {
    const defaultData = dmmServiceListData.map(({ name, href, src, data_link, isNew }) => ({
      name,
      href,
      src,
      data_link,
      isNew,
    }));
    const firstRow: NavItem[] = defaultData.slice(0, 16);

    // その他を削除したリスト
    const filterOtherDataList = defaultData.filter(
      ({ name }) => name !== DMM_SERVICE_MENU.OTHER_SERVICE && name !== DMM_SERVICE_MENU.FOR_BUSINESS
    );

    let secondRow: NavItem[] = [];

    const otherMenuIndex = dmmServiceListData.findIndex(({ name }) => name === DMM_SERVICE_MENU.OTHER_SERVICE);
    if (!isOpenMore) {
      secondRow = dmmServiceListData.slice(16, otherMenuIndex + 1);
    } else {
      const otherFloors = dmmServiceListData[otherMenuIndex].floors;
      const otherTabs = otherFloors.map(({ name, href, src, data_link, isNew }) => ({
        name,
        href,
        src,
        data_link,
        isNew,
      }));
      secondRow = [...dmmServiceListData.slice(16, filterOtherDataList.length), ...otherTabs];
    }

    const businessTab = dmmServiceListData.findIndex(({ name }) => name === DMM_SERVICE_MENU.FOR_BUSINESS);

    const floors: NavItem[] =
      dmmServiceListData[businessTab]?.floors?.map(({ name, href, src, data_link, isNew }) => ({
        name,
        href,
        src,
        data_link,
        isNew,
      })) || [];
    const thirdRow: NavItem[] = [dmmServiceListData[businessTab], ...floors];

    return [firstRow, secondRow, thirdRow];
  }, [dmmServiceListData, isOpenMore]);

  const { isLoggedIn } = useAuth();
  const { isAdultLinkAllowed } = useViewableRatingBlock();
  const isShowFANZAlink = isLoggedIn && isAdultLinkAllowed;

  return (
    <>
      <Styled.DivGlobalColumn>
        <Styled.Anchor
          href={URL.top}
          onClick={() => sendTrackingClickCommon({ clickEventName: "dmmtv_navigation", viaTargetId: "dmmtop" })}
        >
          DMM.com トップへ
        </Styled.Anchor>
        <ul>
          {dmmServiceList[0].map((item, idx) => (
            <NaviItem
              key={item.name}
              src={item.src}
              text={item.name}
              href={item.href}
              viaViewRank={idx + 1}
              viaTargetId={item.data_link}
              isNew={item.isNew}
            />
          ))}
        </ul>
      </Styled.DivGlobalColumn>

      <Styled.DivGlobalColumn>
        <ul>
          <>
            {dmmServiceList[1].map((item, idx) => {
              return item.name !== DMM_SERVICE_MENU.OTHER_SERVICE ? (
                <NaviItem
                  key={item.name}
                  src={item.src}
                  text={item.name}
                  href={item.href}
                  viaViewRank={14 + idx}
                  viaTargetId={item.data_link}
                  isNew={item.isNew}
                />
              ) : (
                <NaviItem
                  key={item.name}
                  src={item.src}
                  text={item.name}
                  viaViewRank={14 + idx}
                  viaTargetId={item.data_link}
                  onItemPressed={openMore}
                  isNew={item.isNew}
                />
              );
            })}
          </>
        </ul>
      </Styled.DivGlobalColumn>

      {/* ３列目 */}
      {isOpenMore && (
        <Styled.DivGlobalColumn>
          <ul>
            <BusinessTitle src={dmmServiceList[2][0].src} title={dmmServiceList[2][0].name} />
            {dmmServiceList[2].slice(1, dmmServiceList[2].length).map((item, idx) => {
              return (
                <NaviItem
                  key={item.name}
                  src={item.src}
                  text={item.name}
                  href={item.href}
                  viaViewRank={28 + idx}
                  viaTargetId={item.data_link}
                  isNew={item.isNew}
                />
              );
            })}
          </ul>

          <Styled.HrLine />

          {isShowFANZAlink && (
            <Styled.Anchor
              isAdult
              href={URL.fanza}
              onClick={() => sendTrackingClickCommon({ clickEventName: "dmmtv_navigation", viaTargetId: "fanzatop" })}
            >
              成人向けへ (FANZA)
            </Styled.Anchor>
          )}
        </Styled.DivGlobalColumn>
      )}
    </>
  );
});

NaviList.displayName = "NaviList";
